import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import GameCard from '@/pages/home/components/GameCard';
import { categories, games } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeCategory = categories.find((c) => c.slug === slug);
  const categoryGames = games.filter((g) => g.category === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!activeCategory) {
    return (
      <>
        <Navbar />
        <div className="pt-20 text-center py-20">
          <p className="text-foreground-500">分类未找到</p>
          <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name} - PG官网游戏大全`,
    description: `PG官网${activeCategory.name}专区，${activeCategory.description}，汇集最热门的${activeCategory.name}作品。`,
    url: `https://www.pg-official.com/category/${slug}`,
  };

  return (
    <>
      <SEOHead
        title={`${activeCategory.name} - PG官网游戏大全 | ${activeCategory.name}官方正版下载`}
        description={`PG官网${activeCategory.name}专区，${activeCategory.description}。提供PG官网${activeCategory.name}正版下载、热门推荐和攻略指南，享受安全稳定的游戏体验。`}
        keywords={`PG官网${activeCategory.name},${activeCategory.name}下载,${activeCategory.name}推荐,PG游戏${activeCategory.name}`}
        canonicalUrl={`https://www.pg-official.com/category/${slug}`}
        schema={schema}
      />

      <Navbar />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '游戏分类', href: '/category/dongzuo' },
              { label: activeCategory.name },
            ]}
          />
        </div>

        <section className="px-4 md:px-6 pb-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100">
                <i className={`${activeCategory.icon} text-accent-600 text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  {activeCategory.name}
                </h1>
                <p className="text-sm text-foreground-500">{activeCategory.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="px-4 md:px-6 pb-8 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 ${
                    cat.slug === slug
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Games Grid */}
        <section className="py-10 px-4 md:px-6" data-product-shop>
          <div className="max-w-7xl mx-auto">
            {categoryGames.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {categoryGames.map((game) => (
                  <GameCard
                    key={game.id}
                    slug={game.slug}
                    name={game.name}
                    image={game.image}
                    category={activeCategory.name}
                    rating={game.rating}
                    players={game.players}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-500">该分类下暂无游戏，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}